// Procedural sound-effect recipes: one designed, layered sound per SfxId.
// Each recipe is built from a transient (click / noise burst), a tonal body
// (oscillators, FM, pitch envelopes, drive) and a tail (filtered noise,
// debris crackle, echoes, reverb send). Recipes only describe the graph; the
// engine handles volume, pitch, pan, voice limits and cleanup.

import type { SfxId } from '../core/types';
import { Voice, hz } from './synth';

export interface SfxDef {
  /** Output level of the recipe (calibrated by measurement in tools/sandbox/audio.ts). */
  gain: number;
  /** Max concurrent voices of this id (oldest is stolen). */
  max?: number;
  /** Min milliseconds between two starts of this id. */
  gap?: number;
  /** Random pitch variation (±fraction) applied per play. Musical stingers use 0. */
  jitter?: number;
  /** Reverb send level. */
  reverb?: number;
  /** Duck the music by this amount (0..1). */
  duck?: number;
  build(v: Voice): void;
}

// ───────────────────────────── Shared layers ─────────────────────────────

/** Bright transient tick. */
function click(v: Voice, at = 0, peak = 0.4, f = 3500, d = 0.01): void {
  v.noise({ at, a: 0.0005, d, peak, filter: { type: 'highpass', f } });
}

/** Pitch-dropping sine (kick-like body / sub boom). */
function thump(v: Voice, at: number, f0: number, f1: number, d: number, peak: number, fdur = d * 0.6): void {
  v.tone({ at, type: 'sine', f: f0, f1, fdur, a: 0.002, d, peak });
}

/** Driven brown-noise blast with a closing lowpass. */
function boom(v: Voice, at: number, peak: number, d: number, lp0 = 2600, lp1 = 160, dist = 4): void {
  v.noise({ at, color: 'brown', a: 0.003, d, peak, filter: { type: 'lowpass', f: lp0, f1: lp1, dur: d * 0.7 }, dist });
}

/** Falling debris / rattle grains. */
function debris(v: Voice, at: number, dur: number, peak: number, f = 3000, rate = 90): void {
  v.crackle({ at, dur, rate, grain: 0.006, peak, fade: true, filter: { type: 'bandpass', f, f1: f * 0.5, dur, q: 1.2 } });
}

/** Inharmonic metallic clank (two FM partials). */
function metal(v: Voice, at: number, f: number, d: number, peak: number): void {
  v.fm({ at, f, ratio: 1.414, index: 4, index1: 0.3, d, peak });
  v.fm({ at, f: f * 2.76, ratio: 1.17, index: 2, index1: 0, d: d * 0.6, peak: peak * 0.5 });
}

/** FM bell. */
function bell(v: Voice, at: number, f: number, d: number, peak: number, ratio = 3.5, index = 2.2): void {
  v.fm({ at, f, ratio, index, index1: 0, d, peak, a: 0.002 });
}

/** A bus that feeds both the dry output and a feedback echo. */
function wet(v: Voice, time: number, fb: number, mix: number, lp = 3500): GainNode {
  const bus = v.gain(1);
  bus.connect(v.echo(time, fb, mix, lp));
  return bus;
}

/** Amplitude-modulation stage: `base` ± `depth` at `rate` Hz for `dur` seconds. */
function trem(v: Voice, rate: number, base: number, depth: number, dur: number, to: AudioNode = v.out): GainNode {
  const g = v.gain(base, to);
  v.lfo(g.gain, rate, depth, 0, dur);
  return g;
}

/** Glittery high-frequency sparkle. */
function sparkle(v: Voice, at: number, dur: number, peak: number, rate = 50): void {
  v.crackle({ at, dur, rate, grain: 0.002, peak, fade: true, filter: { type: 'highpass', f: 7000 } });
}

function arp(v: Voice, freqs: number[], step: number, at: number, type: OscillatorType, d: number, peak: number, to?: AudioNode): void {
  freqs.forEach((f, i) => v.tone({ at: at + i * step, type, f, d, peak, a: 0.003, to }));
}

const N = (...names: string[]): number[] => names.map(hz);

// ───────────────────────────── Recipes ─────────────────────────────

export const SFX: Record<SfxId, SfxDef> = {
  // ── UI ──
  ui_hover: {
    gain: 0.3,
    max: 2,
    gap: 30,
    jitter: 0.015,
    build(v) {
      v.tone({ type: 'triangle', f: 2400, f1: 2900, fdur: 0.02, d: 0.035, peak: 0.35 });
      v.tone({ type: 'sine', f: 1200, d: 0.03, peak: 0.2 });
    },
  },
  ui_click: {
    gain: 0.4,
    max: 3,
    jitter: 0.01,
    build(v) {
      click(v, 0, 0.35, 4000, 0.008);
      v.tone({ type: 'square', f: 1500, f1: 1100, fdur: 0.02, d: 0.045, peak: 0.18, filter: { type: 'lowpass', f: 4000 } });
      v.tone({ type: 'sine', f: 760, d: 0.06, peak: 0.35 });
    },
  },
  ui_back: {
    gain: 0.4,
    max: 2,
    jitter: 0.01,
    build(v) {
      click(v, 0, 0.25, 3000, 0.008);
      v.tone({ type: 'triangle', f: 1100, f1: 700, fdur: 0.07, d: 0.1, peak: 0.45 });
      v.tone({ type: 'sine', f: 550, f1: 350, fdur: 0.07, d: 0.1, peak: 0.25 });
    },
  },
  ui_confirm: {
    gain: 0.42,
    max: 2,
    jitter: 0,
    reverb: 0.2,
    build(v) {
      click(v, 0, 0.25, 4000, 0.008);
      v.tone({ type: 'triangle', f: hz('E6'), d: 0.12, peak: 0.38 });
      v.tone({ type: 'sine', f: hz('E5'), d: 0.12, peak: 0.2 });
      v.tone({ at: 0.06, type: 'triangle', f: hz('B6'), d: 0.22, peak: 0.38 });
      v.tone({ at: 0.06, type: 'sine', f: hz('B5'), d: 0.2, peak: 0.2 });
    },
  },
  ui_deny: {
    gain: 0.4,
    max: 2,
    gap: 60,
    jitter: 0.01,
    build(v) {
      v.tone({ type: 'square', f: 220, d: 0.07, peak: 0.3, filter: { type: 'lowpass', f: 1400 } });
      v.tone({ at: 0.09, type: 'square', f: 165, d: 0.12, peak: 0.3, filter: { type: 'lowpass', f: 1200 } });
      v.noise({ d: 0.04, peak: 0.2, filter: { type: 'bandpass', f: 300, q: 2 } });
    },
  },

  // ── Movement ──
  jump: {
    gain: 0.45,
    max: 2,
    gap: 40,
    jitter: 0.05,
    build(v) {
      v.noise({ color: 'pink', a: 0.01, d: 0.14, peak: 0.6, filter: { type: 'bandpass', f: 700, f1: 2200, dur: 0.1, q: 0.9 } });
      v.tone({ type: 'sine', f: 260, f1: 420, fdur: 0.05, d: 0.07, peak: 0.25 });
      click(v, 0, 0.2, 5000, 0.006);
    },
  },
  double_jump: {
    gain: 0.5,
    max: 2,
    gap: 40,
    jitter: 0.04,
    build(v) {
      v.noise({ a: 0.005, d: 0.26, peak: 0.55, filter: { type: 'bandpass', f: 1200, f1: 3500, dur: 0.06, f2: 1500, dur2: 0.15, q: 1.1 } });
      v.tone({ type: 'sawtooth', f: 90, f1: 170, fdur: 0.12, d: 0.2, peak: 0.25, filter: { type: 'lowpass', f: 900 }, dist: 3 });
      thump(v, 0, 140, 60, 0.12, 0.4);
      v.crackle({ dur: 0.15, rate: 120, peak: 0.2, fade: true, filter: { type: 'bandpass', f: 4000, q: 1.5 } });
    },
  },
  land: {
    gain: 0.5,
    max: 2,
    gap: 50,
    jitter: 0.05,
    build(v) {
      thump(v, 0, 130, 48, 0.14, 0.8, 0.08);
      v.noise({ color: 'brown', d: 0.1, peak: 0.5, filter: { type: 'lowpass', f: 500 } });
      v.fm({ f: 2100, ratio: 1.41, index: 2, index1: 0, d: 0.04, peak: 0.12 });
      click(v, 0, 0.2, 2500, 0.008);
    },
  },
  land_heavy: {
    gain: 0.65,
    max: 2,
    gap: 60,
    jitter: 0.04,
    reverb: 0.2,
    duck: 0.2,
    build(v) {
      thump(v, 0, 100, 32, 0.38, 1, 0.15);
      boom(v, 0, 0.7, 0.3, 900, 200, 2);
      metal(v, 0.005, 240, 0.35, 0.22);
      debris(v, 0.02, 0.2, 0.3, 2500);
      click(v, 0, 0.4, 1800, 0.012);
    },
  },
  dash: {
    gain: 0.5,
    max: 2,
    gap: 50,
    jitter: 0.05,
    reverb: 0.1,
    build(v) {
      v.noise({ a: 0.008, d: 0.2, peak: 0.55, filter: { type: 'bandpass', f: 900, f1: 5000, dur: 0.08, f2: 1800, dur2: 0.12, q: 1.4 } });
      v.tone({ type: 'sawtooth', f: 220, f1: 1300, fdur: 0.07, f2: 500, f2dur: 0.12, d: 0.2, peak: 0.18, filter: { type: 'lowpass', f: 3000 }, dist: 4 });
      v.crackle({ dur: 0.14, rate: 260, grain: 0.003, peak: 0.28, fade: true, filter: { type: 'bandpass', f: 5000, q: 2 } });
      thump(v, 0, 180, 90, 0.08, 0.3);
    },
  },
  footstep: {
    gain: 0.3,
    max: 2,
    gap: 60,
    jitter: 0.07,
    build(v) {
      click(v, 0, 0.25, 2800, 0.006);
      v.tone({ type: 'square', f: 1900, f1: 1400, fdur: 0.01, d: 0.018, peak: 0.08, filter: { type: 'lowpass', f: 5000 } });
      thump(v, 0, 95, 60, 0.05, 0.35);
    },
  },

  // ── Weapons ──
  shoot_pulse: {
    gain: 0.5,
    max: 5,
    gap: 25,
    jitter: 0.04,
    reverb: 0.12,
    build(v) {
      click(v, 0, 0.5, 3000, 0.006);
      v.tone({ type: 'square', f: 1200, f1: 240, fdur: 0.07, d: 0.11, peak: 0.32, filter: { type: 'lowpass', f: 7000, f1: 900, dur: 0.09, q: 3 }, dist: 2 });
      v.tone({ type: 'sawtooth', f: 600, f1: 150, fdur: 0.07, d: 0.09, peak: 0.15, filter: { type: 'lowpass', f: 3000 } });
      thump(v, 0, 160, 48, 0.1, 0.8, 0.06);
      v.noise({ d: 0.035, peak: 0.25, filter: { type: 'highpass', f: 3500 } });
    },
  },
  shoot_scatter: {
    gain: 0.55,
    max: 3,
    gap: 40,
    jitter: 0.04,
    reverb: 0.25,
    duck: 0.15,
    build(v) {
      click(v, 0, 0.6, 2000, 0.01);
      boom(v, 0, 0.9, 0.3, 2600, 220, 4);
      thump(v, 0, 95, 35, 0.26, 0.9, 0.12);
      v.tone({ type: 'square', f: 180, f1: 60, fdur: 0.08, d: 0.1, peak: 0.2, filter: { type: 'lowpass', f: 900 } });
      // Metallic rattle of the pellets / shell.
      v.crackle({ at: 0.015, dur: 0.16, rate: 160, grain: 0.005, peak: 0.45, fade: true, filter: { type: 'bandpass', f: 3800, q: 2.5 } });
      v.fm({ at: 0.01, f: 700, ratio: 2.76, index: 3, index1: 0, d: 0.14, peak: 0.15 });
      v.fm({ at: 0.05, f: 1100, ratio: 1.41, index: 2, index1: 0, d: 0.1, peak: 0.08 });
    },
  },
  shoot_arc: {
    gain: 0.5,
    max: 4,
    gap: 30,
    jitter: 0.04,
    reverb: 0.15,
    build(v) {
      click(v, 0, 0.45, 4000, 0.006);
      v.crackle({ dur: 0.2, rate: 450, grain: 0.003, peak: 0.7, fade: true, filter: { type: 'bandpass', f: 2200, f1: 6000, dur: 0.12, q: 1.8 } });
      v.crackle({ at: 0.01, dur: 0.16, rate: 200, grain: 0.004, peak: 0.4, fade: true, filter: { type: 'bandpass', f: 900, f1: 1600, dur: 0.1, q: 2 } });
      v.tone({ type: 'sawtooth', f: 60, d: 0.15, peak: 0.25, filter: { type: 'lowpass', f: 1500 }, dist: 6 });
      v.tone({ type: 'square', f: 2400, f1: 800, fdur: 0.05, d: 0.06, peak: 0.1, filter: { type: 'lowpass', f: 5000 } });
      thump(v, 0, 140, 60, 0.06, 0.4);
    },
  },
  rail_charge: {
    gain: 0.45,
    max: 1,
    gap: 80,
    jitter: 0.02,
    reverb: 0.15,
    build(v) {
      v.tone({ type: 'sawtooth', f: 160, f1: 1400, fdur: 0.85, a: 0.12, h: 0.7, d: 0.25, peak: 0.25, filter: { type: 'bandpass', f: 400, f1: 3500, dur: 0.85, q: 6 } });
      v.tone({ type: 'sine', f: 320, f1: 2800, fdur: 0.85, a: 0.1, h: 0.7, d: 0.2, peak: 0.2, vib: [9, 25] });
      v.noise({ a: 0.7, d: 0.2, peak: 0.25, filter: { type: 'highpass', f: 2000, f1: 7000, dur: 0.85 } });
      v.crackle({ at: 0.5, dur: 0.45, rate: 60, peak: 0.15, filter: { type: 'bandpass', f: 5000, q: 2 } });
    },
  },
  shoot_rail: {
    gain: 0.6,
    max: 2,
    gap: 60,
    jitter: 0.03,
    reverb: 0.35,
    duck: 0.3,
    build(v) {
      v.noise({ a: 0.0005, d: 0.05, peak: 1, filter: { type: 'highpass', f: 1800 } });
      v.tone({ type: 'square', f: 3600, f1: 180, fdur: 0.06, d: 0.08, peak: 0.35, filter: { type: 'lowpass', f: 9000 }, dist: 3 });
      thump(v, 0, 85, 28, 0.55, 1, 0.25);
      v.noise({ color: 'brown', d: 0.3, peak: 0.5, filter: { type: 'lowpass', f: 1600, f1: 200, dur: 0.2 } });
      const ring = wet(v, 0.09, 0.3, 0.3, 5000);
      v.fm({ f: 1760, ratio: 1.5, index: 1, index1: 0, d: 0.8, peak: 0.18, to: ring });
      v.tone({ type: 'sine', f: 2637, d: 0.7, peak: 0.08, to: ring });
      v.sustain(1.2);
    },
  },
  shoot_rocket: {
    gain: 0.55,
    max: 3,
    gap: 50,
    jitter: 0.04,
    reverb: 0.2,
    build(v) {
      click(v, 0, 0.4, 2500, 0.01);
      thump(v, 0, 320, 80, 0.1, 0.7, 0.05);
      v.noise({ color: 'pink', a: 0.03, h: 0.05, d: 0.4, peak: 0.7, filter: { type: 'bandpass', f: 500, f1: 2400, dur: 0.3, q: 1.2 } });
      v.tone({ type: 'sawtooth', f: 140, f1: 70, fdur: 0.4, a: 0.02, d: 0.4, peak: 0.18, filter: { type: 'lowpass', f: 700 }, dist: 2 });
      v.crackle({ dur: 0.3, rate: 100, peak: 0.2, fade: true, filter: { type: 'bandpass', f: 2500, q: 1.5 } });
    },
  },
  shoot_gravity: {
    gain: 0.55,
    max: 2,
    gap: 60,
    jitter: 0.03,
    reverb: 0.35,
    build(v) {
      v.tone({ type: 'sawtooth', f: 55, a: 0.01, h: 0.15, d: 0.3, peak: 0.45, filter: { type: 'lowpass', f: 180, f1: 1400, dur: 0.12, f2: 160, dur2: 0.3, q: 8 }, dist: 2 });
      thump(v, 0, 110, 38, 0.5, 0.8, 0.4);
      v.noise({ color: 'pink', a: 0.25, d: 0.08, peak: 0.35, filter: { type: 'bandpass', f: 5000, f1: 300, dur: 0.3, q: 2 } });
      v.tone({ type: 'sine', f: 880, f1: 220, fdur: 0.3, d: 0.3, peak: 0.08 });
    },
  },

  // ── Heat ──
  overheat: {
    gain: 0.55,
    max: 1,
    gap: 200,
    jitter: 0.01,
    reverb: 0.2,
    build(v) {
      v.noise({ a: 0.01, h: 0.2, d: 0.8, peak: 0.45, filter: { type: 'highpass', f: 2500 }, filter2: { type: 'lowpass', f: 9000, f1: 3000, dur: 1 } });
      for (let i = 0; i < 3; i++) {
        v.tone({ at: i * 0.16, type: 'square', f: i % 2 ? 660 : 880, a: 0.005, h: 0.08, d: 0.05, peak: 0.18, filter: { type: 'lowpass', f: 2500 } });
      }
      thump(v, 0, 300, 120, 0.25, 0.4);
    },
  },
  vent: {
    gain: 0.5,
    max: 1,
    gap: 150,
    jitter: 0.03,
    reverb: 0.25,
    build(v) {
      click(v, 0, 0.35, 2000, 0.01);
      v.noise({ a: 0.06, h: 0.35, d: 0.7, peak: 0.5, filter: { type: 'bandpass', f: 3000, f1: 1800, dur: 1, q: 0.6 } });
      v.noise({ color: 'pink', a: 0.05, h: 0.3, d: 0.6, peak: 0.35, filter: { type: 'lowpass', f: 900 } });
      thump(v, 0, 420, 180, 0.12, 0.3, 0.1);
    },
  },
  heat_warning: {
    gain: 0.4,
    max: 1,
    gap: 300,
    jitter: 0,
    build(v) {
      for (const at of [0, 0.11]) {
        v.tone({ at, type: 'square', f: 1320, a: 0.003, d: 0.05, peak: 0.22, filter: { type: 'lowpass', f: 3500 } });
        v.tone({ at, type: 'sine', f: 1320, d: 0.06, peak: 0.2 });
      }
    },
  },
  weapon_switch: {
    gain: 0.45,
    max: 2,
    gap: 50,
    jitter: 0.03,
    build(v) {
      click(v, 0, 0.4, 3000, 0.008);
      v.fm({ f: 950, ratio: 1.5, index: 2, index1: 0, d: 0.05, peak: 0.2 });
      v.tone({ at: 0.01, type: 'sine', f: 320, f1: 560, fdur: 0.05, d: 0.07, peak: 0.25 });
      click(v, 0.075, 0.45, 2200, 0.008);
      v.fm({ at: 0.075, f: 1500, ratio: 2.4, index: 1.5, index1: 0, d: 0.06, peak: 0.15 });
      v.tone({ at: 0.075, type: 'sine', f: 120, d: 0.06, peak: 0.4 });
    },
  },

  // ── Impacts ──
  hit_enemy: {
    gain: 0.45,
    max: 4,
    gap: 25,
    jitter: 0.08,
    build(v) {
      v.noise({ d: 0.03, peak: 0.5, filter: { type: 'highpass', f: 2500 } });
      v.fm({ f: 1500, ratio: 2.4, index: 3, index1: 0, d: 0.07, peak: 0.22 });
      v.tone({ type: 'sine', f: 240, f1: 120, fdur: 0.03, d: 0.05, peak: 0.45 });
      v.tone({ type: 'square', f: 600, f1: 300, fdur: 0.02, d: 0.03, peak: 0.1, filter: { type: 'lowpass', f: 3000 } });
    },
  },
  hit_enemy_crit: {
    gain: 0.55,
    max: 3,
    gap: 30,
    jitter: 0.06,
    reverb: 0.1,
    build(v) {
      v.noise({ d: 0.1, peak: 0.6, filter: { type: 'bandpass', f: 1800, q: 1 }, dist: 12 });
      v.fm({ f: 900, ratio: 3.1, index: 4, index1: 0, d: 0.18, peak: 0.25 });
      v.tone({ type: 'square', f: 260, f1: 60, fdur: 0.06, d: 0.1, peak: 0.3, filter: { type: 'lowpass', f: 2500 }, dist: 6 });
      v.tone({ at: 0.005, type: 'sine', f: 2400, d: 0.15, peak: 0.12 });
      thump(v, 0, 160, 50, 0.1, 0.6);
    },
  },
  hit_shield: {
    gain: 0.4,
    max: 4,
    gap: 30,
    jitter: 0.05,
    reverb: 0.25,
    build(v) {
      v.noise({ d: 0.02, peak: 0.3, filter: { type: 'highpass', f: 4000 } });
      v.tone({ type: 'sine', f: 2200, d: 0.3, peak: 0.3 });
      v.tone({ type: 'sine', f: 3300, d: 0.2, peak: 0.14 });
      v.fm({ f: 1650, ratio: 1.5, index: 1.2, index1: 0, d: 0.2, peak: 0.15 });
      v.tone({ type: 'triangle', f: 1100, f1: 900, fdur: 0.08, d: 0.1, peak: 0.15 });
    },
  },
  hit_player: {
    gain: 0.65,
    max: 2,
    gap: 60,
    jitter: 0.04,
    reverb: 0.15,
    duck: 0.2,
    build(v) {
      click(v, 0, 0.6, 2000, 0.01);
      thump(v, 0, 170, 50, 0.22, 0.9, 0.1);
      v.noise({ color: 'brown', d: 0.15, peak: 0.7, filter: { type: 'lowpass', f: 1600 }, dist: 5 });
      v.tone({ type: 'square', f: 140, d: 0.14, peak: 0.25, filter: { type: 'lowpass', f: 1000 }, dist: 3 });
      v.fm({ f: 520, ratio: 2.76, index: 3, index1: 0, d: 0.2, peak: 0.2 });
    },
  },
  shield_break: {
    gain: 0.6,
    max: 1,
    gap: 100,
    jitter: 0.03,
    reverb: 0.4,
    duck: 0.2,
    build(v) {
      // Shatter: a spray of short, random high partials.
      for (let i = 0; i < 14; i++) {
        v.tone({
          at: Math.random() * 0.12,
          type: i % 2 ? 'triangle' : 'sine',
          f: 2000 + Math.random() * 5000,
          d: 0.08 + Math.random() * 0.25,
          peak: 0.06 + Math.random() * 0.06,
        });
      }
      v.noise({ a: 0.002, d: 0.35, peak: 0.45, filter: { type: 'highpass', f: 3500 }, filter2: { type: 'lowpass', f: 12000, f1: 4000, dur: 0.3 } });
      v.tone({ type: 'sine', f: 1500, f1: 180, fdur: 0.4, d: 0.45, peak: 0.3 });
      v.crackle({ dur: 0.25, rate: 200, peak: 0.3, fade: true, filter: { type: 'highpass', f: 4000 } });
      thump(v, 0, 110, 45, 0.25, 0.5);
    },
  },
  shield_regen: {
    gain: 0.45,
    max: 1,
    gap: 200,
    jitter: 0,
    reverb: 0.4,
    build(v) {
      v.tone({ type: 'sine', f: 400, f1: 1600, fdur: 0.35, a: 0.25, d: 0.2, peak: 0.25 });
      v.tone({ type: 'triangle', f: 800, f1: 3200, fdur: 0.35, a: 0.25, d: 0.15, peak: 0.1 });
      v.noise({ a: 0.3, d: 0.1, peak: 0.2, filter: { type: 'bandpass', f: 1000, f1: 6000, dur: 0.35, q: 1.5 } });
      N('C6', 'G6', 'C7').forEach((f, i) => bell(v, 0.32 + i * 0.05, f, 0.4, 0.12));
    },
  },

  // ── Explosions & elemental ──
  explosion_small: {
    gain: 0.55,
    max: 4,
    gap: 30,
    jitter: 0.08,
    reverb: 0.3,
    duck: 0.1,
    build(v) {
      click(v, 0, 0.6, 1500, 0.012);
      boom(v, 0, 0.9, 0.45, 2800, 260, 4);
      thump(v, 0, 120, 38, 0.35, 0.9, 0.18);
      v.noise({ d: 0.06, peak: 0.3, filter: { type: 'highpass', f: 3000 } });
      debris(v, 0.03, 0.3, 0.35, 3200);
    },
  },
  explosion_large: {
    gain: 0.7,
    max: 2,
    gap: 50,
    jitter: 0.05,
    reverb: 0.4,
    duck: 0.55,
    build(v) {
      v.noise({ d: 0.09, peak: 0.7, filter: { type: 'highpass', f: 1500 } });
      thump(v, 0, 80, 24, 1.1, 1, 0.5);
      boom(v, 0, 1, 1.3, 3400, 120, 5);
      v.noise({ at: 0.07, color: 'brown', a: 0.02, d: 1, peak: 0.5, filter: { type: 'lowpass', f: 900, f1: 90, dur: 0.8 } });
      debris(v, 0.04, 0.9, 0.4, 3500);
      v.crackle({ at: 0.15, dur: 0.6, rate: 25, grain: 0.012, peak: 0.3, fade: true, filter: { type: 'lowpass', f: 2500 } });
    },
  },
  chain_zap: {
    gain: 0.45,
    max: 3,
    gap: 40,
    jitter: 0.06,
    reverb: 0.15,
    build(v) {
      click(v, 0, 0.3, 5000, 0.006);
      v.crackle({ dur: 0.12, rate: 500, grain: 0.003, peak: 0.7, fade: true, filter: { type: 'bandpass', f: 3200, q: 2 } });
      v.tone({ type: 'sawtooth', f: 2200, f1: 700, fdur: 0.08, d: 0.1, peak: 0.12, filter: { type: 'bandpass', f: 2000, q: 3 } });
      v.tone({ type: 'square', f: 75, d: 0.1, peak: 0.15, filter: { type: 'lowpass', f: 1800 }, dist: 8 });
    },
  },
  burn_ignite: {
    gain: 0.45,
    max: 3,
    gap: 50,
    jitter: 0.06,
    reverb: 0.2,
    build(v) {
      v.noise({ color: 'pink', a: 0.04, d: 0.35, peak: 0.7, filter: { type: 'bandpass', f: 300, f1: 1400, dur: 0.2, q: 1 } });
      v.crackle({ at: 0.03, dur: 0.45, rate: 70, grain: 0.008, peak: 0.35, fade: true, filter: { type: 'highpass', f: 1800 } });
      v.tone({ type: 'sine', f: 90, f1: 60, fdur: 0.2, a: 0.03, d: 0.2, peak: 0.35 });
    },
  },
  gravity_field: {
    gain: 0.55,
    max: 2,
    gap: 80,
    jitter: 0.03,
    reverb: 0.4,
    build(v) {
      const t = trem(v, 6, 0.65, 0.35, 1.05);
      v.tone({ type: 'sine', f: 95, f1: 48, fdur: 0.6, a: 0.08, d: 0.9, peak: 0.7, to: t });
      v.tone({ type: 'sawtooth', f: 48, a: 0.1, d: 0.8, peak: 0.3, filter: { type: 'lowpass', f: 300, f1: 900, dur: 0.3, f2: 200, dur2: 0.5, q: 6 }, to: t });
      v.noise({ color: 'pink', a: 0.4, d: 0.3, peak: 0.3, filter: { type: 'bandpass', f: 3000, f1: 400, dur: 0.7, q: 2 } });
    },
  },
  gravity_implode: {
    gain: 0.6,
    max: 2,
    gap: 60,
    jitter: 0.04,
    reverb: 0.4,
    duck: 0.3,
    build(v) {
      v.noise({ color: 'pink', a: 0.28, d: 0.04, peak: 0.55, filter: { type: 'bandpass', f: 300, f1: 5000, dur: 0.3, q: 2 } });
      v.tone({ type: 'sine', f: 200, f1: 900, fdur: 0.3, a: 0.28, d: 0.03, peak: 0.2 });
      click(v, 0.3, 0.5, 1500, 0.012);
      thump(v, 0.3, 140, 30, 0.5, 1, 0.25);
      boom(v, 0.3, 0.7, 0.5, 2000, 150, 4);
      v.crackle({ at: 0.32, dur: 0.3, rate: 120, peak: 0.3, fade: true, filter: { type: 'bandpass', f: 2500, q: 1.2 } });
    },
  },

  // ── Enemies ──
  enemy_telegraph: {
    gain: 0.4,
    max: 3,
    gap: 80,
    jitter: 0.02,
    build(v) {
      v.tone({ type: 'square', f: 1046, a: 0.002, d: 0.06, peak: 0.25, filter: { type: 'lowpass', f: 4000 } });
      v.tone({ at: 0.08, type: 'square', f: 1568, d: 0.08, peak: 0.25, filter: { type: 'lowpass', f: 4000 } });
      v.tone({ at: 0.08, type: 'sine', f: 2093, d: 0.1, peak: 0.1 });
    },
  },
  enemy_shoot: {
    gain: 0.38,
    max: 4,
    gap: 35,
    jitter: 0.06,
    reverb: 0.1,
    build(v) {
      click(v, 0, 0.3, 3500, 0.006);
      v.tone({ type: 'sawtooth', f: 900, f1: 260, fdur: 0.06, d: 0.09, peak: 0.3, filter: { type: 'lowpass', f: 4000, f1: 1000, dur: 0.07 }, dist: 2 });
      v.tone({ type: 'sine', f: 200, f1: 70, fdur: 0.05, d: 0.07, peak: 0.5 });
    },
  },
  enemy_laser_charge: {
    gain: 0.42,
    max: 2,
    gap: 100,
    jitter: 0.03,
    reverb: 0.2,
    build(v) {
      const t = trem(v, 18, 0.6, 0.4, 0.75);
      v.tone({ type: 'sine', f: 300, f1: 1400, fdur: 0.6, a: 0.1, h: 0.45, d: 0.15, peak: 0.3, to: t });
      v.tone({ type: 'sawtooth', f: 150, f1: 700, fdur: 0.6, a: 0.1, h: 0.45, d: 0.1, peak: 0.12, filter: { type: 'bandpass', f: 500, f1: 2500, dur: 0.6, q: 5 }, to: t });
      v.noise({ a: 0.5, d: 0.15, peak: 0.15, filter: { type: 'highpass', f: 3000, f1: 8000, dur: 0.6 } });
    },
  },
  enemy_laser_fire: {
    gain: 0.5,
    max: 2,
    gap: 80,
    jitter: 0.03,
    reverb: 0.25,
    build(v) {
      v.noise({ d: 0.08, peak: 0.5, filter: { type: 'highpass', f: 2000 } });
      v.tone({ type: 'sawtooth', f: 110, a: 0.005, h: 0.25, d: 0.3, peak: 0.3, filter: { type: 'lowpass', f: 3200, f1: 900, dur: 0.5 }, dist: 4 });
      v.tone({ type: 'sawtooth', f: 165.5, a: 0.005, h: 0.25, d: 0.3, peak: 0.2, filter: { type: 'lowpass', f: 2500 } });
      v.noise({ color: 'pink', a: 0.01, h: 0.2, d: 0.3, peak: 0.25, filter: { type: 'bandpass', f: 1500, q: 0.8 } });
      thump(v, 0, 90, 40, 0.2, 0.6);
    },
  },
  enemy_death: {
    gain: 0.5,
    max: 4,
    gap: 30,
    jitter: 0.07,
    reverb: 0.25,
    build(v) {
      v.noise({ d: 0.2, peak: 0.6, filter: { type: 'lowpass', f: 2500 }, dist: 10 });
      v.fm({ f: 320, ratio: 2.76, index: 4, index1: 0, d: 0.3, peak: 0.22 });
      thump(v, 0, 160, 40, 0.25, 0.8, 0.15);
      debris(v, 0.02, 0.25, 0.35, 2800);
      v.tone({ type: 'sawtooth', f: 1600, f1: 90, fdur: 0.3, d: 0.32, peak: 0.12, filter: { type: 'lowpass', f: 3000 } });
    },
  },
  enemy_spawn: {
    gain: 0.42,
    max: 3,
    gap: 60,
    jitter: 0.04,
    reverb: 0.35,
    build(v) {
      v.tone({ type: 'sine', f: 180, f1: 1500, fdur: 0.35, a: 0.3, d: 0.08, peak: 0.3 });
      v.noise({ a: 0.3, d: 0.08, peak: 0.25, filter: { type: 'bandpass', f: 500, f1: 5000, dur: 0.35, q: 2 } });
      click(v, 0.36, 0.5, 2500, 0.01);
      thump(v, 0.36, 220, 60, 0.15, 0.6);
      bell(v, 0.36, 1320, 0.35, 0.12, 1.41);
    },
  },
  drone_buzz: {
    gain: 0.3,
    max: 2,
    gap: 120,
    jitter: 0.05,
    build(v) {
      const t = trem(v, 38, 0.6, 0.4, 0.32);
      v.tone({ type: 'sawtooth', f: 180, a: 0.02, h: 0.15, d: 0.12, peak: 0.3, filter: { type: 'bandpass', f: 1300, q: 2 }, to: t, vib: [7, 30] });
      v.tone({ type: 'sawtooth', f: 183.5, a: 0.02, h: 0.15, d: 0.12, peak: 0.2, filter: { type: 'bandpass', f: 1300, q: 2 }, to: t });
    },
  },

  // ── Bosses ──
  boss_roar: {
    gain: 0.6,
    max: 1,
    gap: 400,
    jitter: 0.03,
    reverb: 0.35,
    duck: 0.6,
    build(v) {
      const bus = v.chain({ filter: { type: 'lowpass', f: 1400, f1: 2400, dur: 0.5, f2: 600, dur2: 1, q: 2 }, dist: 8, gain: 0.5 });
      for (const f of [55, 55.7, 82.5]) {
        v.tone({ type: 'sawtooth', f, f1: f * 1.3, fdur: 0.45, f2: f * 0.8, f2dur: 1, a: 0.15, h: 0.7, d: 0.7, peak: 0.5, vib: [5.5, 30], to: bus });
      }
      v.noise({ color: 'pink', a: 0.2, h: 0.6, d: 0.7, peak: 0.5, filter: { type: 'lowpass', f: 700 }, filter2: { type: 'highpass', f: 80 }, dist: 3 });
      v.tone({ type: 'sine', f: 41, f1: 55, fdur: 0.45, f2: 35, f2dur: 1, a: 0.15, h: 0.7, d: 0.6, peak: 0.6 });
    },
  },
  boss_slam: {
    gain: 0.7,
    max: 2,
    gap: 100,
    jitter: 0.04,
    reverb: 0.45,
    duck: 0.5,
    build(v) {
      click(v, 0, 0.6, 1200, 0.015);
      thump(v, 0, 70, 24, 0.8, 1, 0.35);
      boom(v, 0, 0.9, 0.7, 1800, 120, 4);
      metal(v, 0, 180, 0.7, 0.25);
      debris(v, 0.03, 0.6, 0.35, 2600);
    },
  },
  boss_phase: {
    gain: 0.65,
    max: 1,
    gap: 500,
    jitter: 0,
    reverb: 0.5,
    duck: 0.6,
    build(v) {
      const swell = v.chain({ filter: { type: 'lowpass', f: 400, f1: 3000, dur: 0.8, q: 4 }, dist: 2 });
      for (const f of N('C3', 'Eb3', 'G3', 'C4')) {
        v.tone({ type: 'sawtooth', f, a: 0.6, h: 0.2, d: 0.9, peak: 0.12, detune: Math.random() * 10 - 5, to: swell });
      }
      v.noise({ a: 0.7, d: 0.05, peak: 0.3, filter: { type: 'highpass', f: 1000, f1: 6000, dur: 0.75 } });
      click(v, 0.8, 0.6, 1500, 0.015);
      thump(v, 0.8, 90, 30, 0.8, 1, 0.4);
      boom(v, 0.8, 0.7, 0.7, 2500, 150, 4);
      bell(v, 0.8, hz('C5'), 1.2, 0.15);
    },
  },
  boss_death: {
    gain: 0.65,
    max: 1,
    gap: 1000,
    jitter: 0,
    reverb: 0.5,
    duck: 0.8,
    build(v) {
      // Chain of failing-system explosions, then the core blows.
      [0, 0.28, 0.5, 0.68, 0.85, 1.0].forEach((at, i) => {
        boom(v, at, 0.5, 0.5, 2600, 200, 4);
        thump(v, at, 110 - i * 6, 35, 0.4, 0.55, 0.2);
        debris(v, at + 0.02, 0.3, 0.2, 3000);
      });
      v.tone({ type: 'sawtooth', f: 600, f1: 30, fdur: 2.2, a: 0.05, d: 2.4, peak: 0.2, filter: { type: 'lowpass', f: 3000, f1: 200, dur: 2.2 }, dist: 3 });
      v.noise({ at: 1.3, d: 0.1, peak: 0.7, filter: { type: 'highpass', f: 1500 } });
      thump(v, 1.3, 70, 20, 1.8, 1, 0.8);
      boom(v, 1.3, 1, 2, 4000, 100, 5);
      debris(v, 1.34, 1.4, 0.35, 3500, 70);
    },
  },

  // ── Loot ──
  orb_open: {
    gain: 0.5,
    max: 2,
    gap: 80,
    jitter: 0,
    reverb: 0.4,
    build(v) {
      v.noise({ d: 0.06, peak: 0.6, filter: { type: 'highpass', f: 5000 } });
      [3100, 4270, 5650, 7020].forEach((f, i) => v.tone({ at: i * 0.002, type: 'sine', f, d: 0.35 + i * 0.08, peak: 0.1 }));
      v.tone({ type: 'triangle', f: 1200, f1: 600, fdur: 0.05, d: 0.08, peak: 0.2 });
      const shimmer = wet(v, 0.13, 0.35, 0.4, 6000);
      arp(v, N('C6', 'E6', 'G6', 'C7', 'E7'), 0.045, 0.06, 'triangle', 0.4, 0.12, shimmer);
      v.sustain(1.3);
    },
  },
  loot_reveal: {
    gain: 0.45,
    max: 2,
    gap: 60,
    jitter: 0,
    reverb: 0.4,
    build(v) {
      const bus = wet(v, 0.14, 0.3, 0.3, 5000);
      N('C5', 'E5', 'G5', 'C6').forEach((f, i) => {
        v.tone({ at: i * 0.07, type: 'triangle', f, d: 0.45, peak: 0.25, to: bus });
        v.tone({ at: i * 0.07, type: 'sine', f: f * 2, d: 0.25, peak: 0.08, to: bus });
      });
      v.tone({ at: 0.28, type: 'sine', f: hz('C7'), d: 0.6, peak: 0.08, to: bus });
      v.sustain(1.2);
    },
  },
  loot_rare: {
    gain: 0.5,
    max: 1,
    gap: 150,
    jitter: 0,
    reverb: 0.5,
    duck: 0.2,
    build(v) {
      const bus = wet(v, 0.15, 0.35, 0.3, 6000);
      arp(v, N('C5', 'E5', 'G5', 'B5', 'D6', 'G6'), 0.05, 0, 'triangle', 0.35, 0.2, bus);
      const pad = v.chain({ filter: { type: 'lowpass', f: 2500, f1: 5000, dur: 0.4, at: 0.3, q: 2 }, to: bus });
      for (const f of N('C5', 'E5', 'G5', 'B5', 'D6')) {
        v.tone({ at: 0.3, type: 'sawtooth', f, a: 0.02, h: 0.2, d: 0.9, peak: 0.06, detune: Math.random() * 12 - 6, to: pad });
        v.tone({ at: 0.3, type: 'sine', f, a: 0.02, h: 0.2, d: 1, peak: 0.05, to: bus });
      }
      bell(v, 0.3, hz('C7'), 1, 0.1);
      bell(v, 0.34, hz('G7'), 0.9, 0.07);
      sparkle(v, 0.3, 0.6, 0.15, 40);
      v.sustain(2);
    },
  },
  equip: {
    gain: 0.45,
    max: 2,
    gap: 60,
    jitter: 0.02,
    build(v) {
      click(v, 0, 0.45, 2500, 0.01);
      v.fm({ f: 600, ratio: 1.5, index: 2.5, index1: 0, d: 0.1, peak: 0.25 });
      thump(v, 0, 140, 90, 0.08, 0.5);
      v.tone({ at: 0.07, type: 'sine', f: 880, d: 0.15, peak: 0.2 });
      v.tone({ at: 0.1, type: 'sine', f: 1320, d: 0.2, peak: 0.18 });
    },
  },
  salvage: {
    gain: 0.45,
    max: 2,
    gap: 60,
    jitter: 0.03,
    reverb: 0.2,
    build(v) {
      v.crackle({ dur: 0.22, rate: 300, grain: 0.003, peak: 0.4, fade: true, filter: { type: 'bandpass', f: 2500, f1: 1200, dur: 0.2, q: 1.5 } });
      v.tone({ type: 'sawtooth', f: 1400, f1: 250, fdur: 0.2, d: 0.22, peak: 0.12, filter: { type: 'lowpass', f: 3000 } });
      v.noise({ a: 0.02, d: 0.18, peak: 0.25, filter: { type: 'bandpass', f: 3000, f1: 800, dur: 0.18, q: 1.2 } });
      v.tone({ at: 0.22, type: 'triangle', f: 1760, d: 0.15, peak: 0.2 });
      v.tone({ at: 0.27, type: 'triangle', f: 2349, d: 0.25, peak: 0.18 });
    },
  },
  scrap_pickup: {
    gain: 0.4,
    max: 4,
    gap: 30,
    jitter: 0.03,
    build(v) {
      v.tone({ type: 'square', f: hz('B5'), d: 0.05, peak: 0.14, filter: { type: 'lowpass', f: 4500 } });
      v.tone({ at: 0.045, type: 'square', f: hz('E6'), d: 0.12, peak: 0.14, filter: { type: 'lowpass', f: 4500 } });
      v.tone({ at: 0.045, type: 'sine', f: hz('E7'), d: 0.1, peak: 0.07 });
    },
  },
  buy: {
    gain: 0.45,
    max: 2,
    gap: 80,
    jitter: 0,
    reverb: 0.2,
    build(v) {
      click(v, 0, 0.3, 3000, 0.008);
      thump(v, 0, 110, 80, 0.06, 0.3);
      v.tone({ type: 'triangle', f: hz('E6'), d: 0.08, peak: 0.25 });
      v.tone({ at: 0.07, type: 'triangle', f: hz('A6'), d: 0.12, peak: 0.25 });
      v.tone({ at: 0.14, type: 'triangle', f: hz('E7'), d: 0.3, peak: 0.22 });
      v.crackle({ at: 0.02, dur: 0.18, rate: 120, grain: 0.004, peak: 0.2, fade: true, filter: { type: 'highpass', f: 6000 } });
    },
  },
  reroll: {
    gain: 0.45,
    max: 1,
    gap: 120,
    jitter: 0,
    reverb: 0.2,
    build(v) {
      const scale = N('C5', 'D5', 'E5', 'G5', 'A5', 'C6', 'D6', 'E6', 'G6');
      for (let i = 0; i < 8; i++) {
        const f = scale[Math.min(scale.length - 1, i + (Math.random() < 0.4 ? 1 : 0))];
        v.tone({ at: i * 0.04, type: 'square', f, d: 0.05, peak: 0.1, filter: { type: 'lowpass', f: 3500 } });
      }
      v.noise({ a: 0.1, d: 0.2, peak: 0.2, filter: { type: 'bandpass', f: 600, f1: 3000, dur: 0.3, q: 1.5 } });
      v.tone({ at: 0.34, type: 'triangle', f: hz('G6'), d: 0.2, peak: 0.2 });
    },
  },
  heal: {
    gain: 0.45,
    max: 1,
    gap: 150,
    jitter: 0,
    reverb: 0.45,
    build(v) {
      for (const f of N('C5', 'E5', 'G5')) v.tone({ type: 'sine', f, a: 0.06, h: 0.1, d: 0.5, peak: 0.14, vib: [5, 6] });
      v.tone({ at: 0.1, type: 'triangle', f: hz('C6'), a: 0.05, d: 0.5, peak: 0.08 });
      v.noise({ a: 0.1, d: 0.3, peak: 0.1, filter: { type: 'bandpass', f: 2000, f1: 5000, dur: 0.3, q: 1 } });
      bell(v, 0.15, hz('C7'), 0.5, 0.06);
    },
  },
  door_unlock: {
    gain: 0.5,
    max: 1,
    gap: 200,
    jitter: 0,
    reverb: 0.3,
    build(v) {
      click(v, 0, 0.5, 1500, 0.012);
      thump(v, 0, 110, 70, 0.12, 0.6);
      v.tone({ type: 'sawtooth', f: 120, f1: 260, fdur: 0.25, a: 0.02, h: 0.15, d: 0.1, peak: 0.14, filter: { type: 'lowpass', f: 900 } });
      click(v, 0.3, 0.5, 2500, 0.01);
      v.fm({ at: 0.3, f: 800, ratio: 1.41, index: 2, index1: 0, d: 0.15, peak: 0.2 });
      v.tone({ at: 0.36, type: 'triangle', f: hz('C6'), d: 0.2, peak: 0.18 });
      v.tone({ at: 0.44, type: 'triangle', f: hz('G6'), d: 0.35, peak: 0.18 });
    },
  },
  room_clear: {
    gain: 0.5,
    max: 1,
    gap: 500,
    jitter: 0,
    reverb: 0.45,
    duck: 0.25,
    build(v) {
      const stab = v.chain({ filter: { type: 'lowpass', f: 3000, f1: 1200, dur: 0.5 } });
      for (const f of N('A4', 'C#5', 'E5')) {
        v.tone({ type: 'sawtooth', f, a: 0.01, h: 0.1, d: 0.6, peak: 0.08, detune: 6, to: stab });
        v.tone({ type: 'sawtooth', f, a: 0.01, h: 0.1, d: 0.6, peak: 0.06, detune: -6, to: stab });
      }
      const bus = wet(v, 0.15, 0.3, 0.3, 6000);
      arp(v, N('A5', 'C#6', 'E6', 'A6'), 0.05, 0.05, 'triangle', 0.4, 0.14, bus);
      bell(v, 0.25, hz('A6'), 0.9, 0.1);
      v.tone({ type: 'sine', f: 110, d: 0.4, peak: 0.4 });
      v.noise({ a: 0.02, d: 0.8, peak: 0.1, filter: { type: 'highpass', f: 6000 } });
      v.sustain(1.6);
    },
  },
  challenge_complete: {
    gain: 0.5,
    max: 1,
    gap: 400,
    jitter: 0,
    reverb: 0.4,
    duck: 0.2,
    build(v) {
      N('G5', 'C6', 'E6', 'G6').forEach((f, i) => {
        const last = i === 3;
        v.tone({ at: i * 0.08, type: 'square', f, d: last ? 0.5 : 0.12, peak: 0.1, filter: { type: 'lowpass', f: 3500 } });
        v.tone({ at: i * 0.08, type: 'triangle', f, d: last ? 0.6 : 0.14, peak: 0.12 });
      });
      bell(v, 0.24, hz('G7'), 0.7, 0.08);
      sparkle(v, 0.24, 0.4, 0.12);
    },
  },
  challenge_fail: {
    gain: 0.45,
    max: 1,
    gap: 400,
    jitter: 0,
    reverb: 0.35,
    build(v) {
      const notes = N('E5', 'C5', 'A4');
      notes.forEach((f, i) => {
        const d = i === 2 ? 0.5 : 0.18;
        v.tone({ at: i * 0.16, type: 'square', f, d, peak: 0.12, filter: { type: 'lowpass', f: 2000 }, vib: i === 2 ? [5, 20] : undefined });
        v.tone({ at: i * 0.16, type: 'square', f, d, peak: 0.06, detune: 12, filter: { type: 'lowpass', f: 1600 } });
        v.tone({ at: i * 0.16, type: 'sine', f: f / 2, d, peak: 0.12 });
      });
    },
  },

  // ── Abilities ──
  arc_dash: {
    gain: 0.6,
    max: 1,
    gap: 100,
    jitter: 0.03,
    reverb: 0.3,
    duck: 0.15,
    build(v) {
      v.crackle({ dur: 0.3, rate: 500, grain: 0.003, peak: 0.6, fade: true, filter: { type: 'bandpass', f: 1800, f1: 6000, dur: 0.2, q: 1.5 } });
      v.tone({ type: 'sawtooth', f: 300, f1: 2200, fdur: 0.12, f2: 900, f2dur: 0.15, d: 0.28, peak: 0.2, dist: 5, filter: { type: 'lowpass', f: 6000 } });
      v.noise({ a: 0.01, d: 0.3, peak: 0.45, filter: { type: 'bandpass', f: 800, f1: 5000, dur: 0.1, f2: 1500, dur2: 0.18, q: 1.2 } });
      thump(v, 0, 200, 60, 0.15, 0.6);
      v.tone({ at: 0.22, type: 'square', f: 1800, f1: 400, fdur: 0.06, d: 0.08, peak: 0.12, filter: { type: 'lowpass', f: 5000 } });
    },
  },
  siege_field: {
    gain: 0.6,
    max: 1,
    gap: 200,
    jitter: 0,
    reverb: 0.4,
    duck: 0.25,
    build(v) {
      click(v, 0, 0.5, 1500, 0.012);
      thump(v, 0, 80, 40, 0.4, 0.9, 0.2);
      metal(v, 0.02, 220, 0.4, 0.2);
      const hum = v.chain({ filter: { type: 'lowpass', f: 300, f1: 2000, dur: 0.4, at: 0.1, q: 5 } });
      for (const f of [110, 165, 220]) v.tone({ at: 0.1, type: 'sawtooth', f, a: 0.25, h: 0.2, d: 0.6, peak: 0.07, to: hum });
      v.tone({ at: 0.1, type: 'sine', f: 880, f1: 1760, fdur: 0.4, a: 0.3, d: 0.4, peak: 0.08 });
      v.noise({ at: 0.1, color: 'pink', a: 0.3, d: 0.4, peak: 0.2, filter: { type: 'bandpass', f: 400, f1: 2400, dur: 0.5, q: 1.2 } });
    },
  },
  phase_echo: {
    gain: 0.55,
    max: 1,
    gap: 150,
    jitter: 0.02,
    reverb: 0.5,
    build(v) {
      const bus = wet(v, 0.12, 0.55, 0.55, 3000);
      v.tone({ type: 'triangle', f: 1400, f1: 350, fdur: 0.3, d: 0.35, peak: 0.25, vib: [12, 40], to: bus });
      v.noise({ color: 'pink', a: 0.15, d: 0.1, peak: 0.25, filter: { type: 'bandpass', f: 4000, f1: 600, dur: 0.25, q: 3 } });
      v.tone({ at: 0.05, type: 'sine', f: 660, f1: 1320, fdur: 0.2, d: 0.25, peak: 0.1, to: bus });
      v.sustain(1.4);
    },
  },
  overload: {
    gain: 0.6,
    max: 1,
    gap: 300,
    jitter: 0,
    reverb: 0.45,
    duck: 0.55,
    build(v) {
      const surge = v.chain({ filter: { type: 'lowpass', f: 600, f1: 6000, dur: 0.5 }, dist: 6 });
      for (const f of [100, 150.5, 201]) v.tone({ type: 'sawtooth', f, f1: f * 6, fdur: 0.5, a: 0.45, h: 0.05, d: 0.05, peak: 0.12, to: surge });
      v.crackle({ dur: 0.55, rate: 300, peak: 0.35, filter: { type: 'highpass', f: 2500 } });
      v.noise({ a: 0.45, d: 0.05, peak: 0.3, filter: { type: 'highpass', f: 1000, f1: 6000, dur: 0.5 } });
      click(v, 0.52, 0.7, 1500, 0.015);
      thump(v, 0.52, 100, 28, 0.7, 1, 0.3);
      boom(v, 0.52, 0.8, 0.8, 3000, 150, 4);
      debris(v, 0.55, 0.5, 0.3, 3500);
      v.tone({ at: 0.52, type: 'sawtooth', f: 880, f1: 110, fdur: 0.5, d: 0.55, peak: 0.12, dist: 3, filter: { type: 'lowpass', f: 4000 } });
    },
  },
  fortress_protocol: {
    gain: 0.6,
    max: 1,
    gap: 300,
    jitter: 0,
    reverb: 0.4,
    duck: 0.4,
    build(v) {
      for (let i = 0; i < 3; i++) {
        const at = i * 0.14;
        click(v, at, 0.45, 1800, 0.012);
        metal(v, at, 160 * (1 + i * 0.12), 0.3, 0.2);
        thump(v, at, 120, 50, 0.2, 0.6);
      }
      const hum = v.chain({ filter: { type: 'lowpass', f: 200, f1: 1500, dur: 0.5, at: 0.4 }, dist: 3 });
      for (const f of [55, 82.5, 110]) v.tone({ at: 0.4, type: 'sawtooth', f, a: 0.3, h: 0.3, d: 0.6, peak: 0.1, to: hum });
      v.tone({ at: 0.4, type: 'sine', f: 220, f1: 440, fdur: 0.4, a: 0.3, d: 0.5, peak: 0.12 });
      v.noise({ at: 0.4, color: 'brown', a: 0.3, d: 0.5, peak: 0.25, filter: { type: 'lowpass', f: 600 } });
    },
  },
  eclipse: {
    gain: 0.6,
    max: 1,
    gap: 300,
    jitter: 0,
    reverb: 0.55,
    duck: 0.7,
    build(v) {
      thump(v, 0, 120, 30, 1.4, 0.9, 1.2);
      const pad = v.chain({ filter: { type: 'lowpass', f: 300, f1: 2500, dur: 0.6, f2: 400, dur2: 0.8, q: 4 } });
      for (const f of N('C3', 'F#3', 'B3')) v.tone({ type: 'sawtooth', f, a: 0.5, h: 0.3, d: 1, peak: 0.08, detune: Math.random() * 14 - 7, to: pad });
      v.noise({ a: 0.5, d: 0.06, peak: 0.35, filter: { type: 'bandpass', f: 200, f1: 4000, dur: 0.55, q: 1.5 } });
      v.noise({ at: 0.55, d: 0.08, peak: 0.6, filter: { type: 'highpass', f: 2000 } });
      boom(v, 0.55, 0.7, 1, 1500, 80, 3);
      sparkle(v, 0.55, 1, 0.15, 60);
      bell(v, 0.55, hz('B6'), 1.5, 0.12, 1.41);
    },
  },

  // ── Feedback / meta ──
  ultimate_ready: {
    gain: 0.5,
    max: 1,
    gap: 500,
    jitter: 0,
    reverb: 0.45,
    build(v) {
      const bus = wet(v, 0.16, 0.3, 0.3, 6000);
      N('D5', 'A5', 'D6', 'F#6', 'A6').forEach((f, i) => {
        v.tone({ at: i * 0.06, type: 'triangle', f, d: 0.4, peak: 0.16, to: bus });
        v.tone({ at: i * 0.06, type: 'square', f: f / 2, d: 0.2, peak: 0.05, filter: { type: 'lowpass', f: 2500 }, to: bus });
      });
      for (const f of N('D6', 'F#6', 'A6')) v.tone({ at: 0.3, type: 'sine', f, a: 0.02, h: 0.2, d: 0.8, peak: 0.08, to: bus });
      bell(v, 0.3, hz('D7'), 1, 0.1);
      v.noise({ a: 0.25, d: 0.2, peak: 0.1, filter: { type: 'highpass', f: 5000 } });
      v.sustain(1.8);
    },
  },
  signature_ready: {
    gain: 0.38,
    max: 1,
    gap: 300,
    jitter: 0,
    reverb: 0.3,
    build(v) {
      v.tone({ type: 'triangle', f: hz('G6'), d: 0.12, peak: 0.2 });
      v.tone({ at: 0.06, type: 'triangle', f: hz('D7'), d: 0.25, peak: 0.2 });
      v.tone({ at: 0.06, type: 'sine', f: hz('G7'), d: 0.2, peak: 0.06 });
    },
  },
  synergy: {
    gain: 0.5,
    max: 1,
    gap: 300,
    jitter: 0,
    reverb: 0.5,
    build(v) {
      const bus = wet(v, 0.16, 0.35, 0.3, 6000);
      N('C6', 'G6', 'E7').forEach((f, i) => v.fm({ at: i * 0.05, f, ratio: i === 1 ? 1.41 : 3.5, index: 2, index1: 0, d: 0.9, peak: 0.12, to: bus }));
      v.tone({ type: 'sine', f: hz('C5'), a: 0.02, d: 0.8, peak: 0.1, to: bus });
      sparkle(v, 0.05, 0.5, 0.12);
      v.sustain(1.7);
    },
  },
  death_player: {
    gain: 0.6,
    max: 1,
    gap: 1000,
    jitter: 0,
    reverb: 0.45,
    duck: 0.8,
    build(v) {
      const down = v.chain({ filter: { type: 'lowpass', f: 4000, f1: 150, dur: 1.6 }, dist: 4 });
      for (const f of [220, 330, 440]) v.tone({ type: 'sawtooth', f, f1: 25, fdur: 1.6, a: 0.005, h: 0.2, d: 1.4, peak: 0.1, to: down });
      v.tone({ type: 'square', f: 440, d: 0.15, peak: 0.1, filter: { type: 'lowpass', f: 2000 } });
      v.tone({ at: 0.25, type: 'square', f: 330, d: 0.2, peak: 0.1, filter: { type: 'lowpass', f: 2000 } });
      v.tone({ at: 0.55, type: 'square', f: 220, d: 0.4, peak: 0.1, filter: { type: 'lowpass', f: 2000 } });
      thump(v, 0, 110, 30, 0.6, 0.9, 0.3);
      boom(v, 0, 0.6, 0.7, 2000, 150, 3);
      v.crackle({ dur: 1.2, rate: 50, grain: 0.006, peak: 0.3, fade: true, filter: { type: 'bandpass', f: 2500, q: 1.2 } });
      click(v, 1.6, 0.3, 2000, 0.01);
    },
  },
  run_victory: {
    gain: 0.5,
    max: 1,
    gap: 2000,
    jitter: 0,
    reverb: 0.5,
    duck: 0.85,
    build(v) {
      const chords: [number, string[], string][] = [
        [0, ['C4', 'E4', 'G4', 'C5'], 'G5'],
        [0.42, ['F4', 'A4', 'C5', 'F5'], 'A5'],
        [0.84, ['G4', 'B4', 'D5', 'G5'], 'B5'],
        [1.26, ['C5', 'E5', 'G5', 'C6'], 'C6'],
      ];
      chords.forEach(([at, names, top], i) => {
        const last = i === chords.length - 1;
        const brass = v.chain({ filter: { type: 'lowpass', f: 900, f1: 3500, dur: 0.15, at, q: 1 } });
        for (const f of N(...names)) {
          v.tone({ at, type: 'sawtooth', f, a: 0.04, h: last ? 0.9 : 0.3, d: last ? 1.2 : 0.4, peak: 0.05, detune: 7, to: brass });
          v.tone({ at, type: 'sawtooth', f, a: 0.04, h: last ? 0.9 : 0.3, d: last ? 1.2 : 0.4, peak: 0.05, detune: -7, to: brass });
        }
        v.tone({ at, type: 'triangle', f: hz(top), a: 0.02, h: last ? 0.8 : 0.25, d: last ? 1 : 0.2, peak: 0.15, vib: [5.5, 12] });
        thump(v, at, 98, 80, 0.4, 0.5, 0.2);
      });
      v.noise({ at: 1.26, a: 0.01, d: 1.6, peak: 0.2, filter: { type: 'highpass', f: 6000 } });
      sparkle(v, 1.26, 1.2, 0.12);
      v.sustain(3.2);
    },
  },
};

export const SFX_IDS = Object.keys(SFX) as SfxId[];

/** Build a sound into any context. The returned voice is already armed for cleanup. */
export function buildSfx(ctx: BaseAudioContext, id: SfxId, dest: AudioNode, t: number, pitch = 1): Voice | null {
  const def = SFX[id];
  if (!def) return null;
  const v = new Voice(ctx, dest, t, pitch);
  def.build(v);
  v.finish();
  return v;
}
